import { useCallback } from 'react';
import * as React from 'react';

import { Icon, Loader, MultiSelect, PlanBadge, tokens } from '@hotjar/hotsauce';
import styled from '@emotion/styled';

import type { Site } from '@hotjar/site';
import type { SlackApi, SlackSettings as Settings } from '@hotjar/integrations';
import { EnableSlackModal, SlackFeature, useSlackState } from '@hotjar/integrations';
import { tagRecording } from '@hotjar/webapp/tracking/tools/hotjar';
import { ErrorBoundary } from '@hotjar/error-handler';
import type { KnownReferrer } from '@hotjar/webapp/features/Billing/PlanComparisonModal/types';

import type { ProductType } from '@hotjar/webapp/domains/common/product/types';
import { useAuthorization } from '@hotjar/webapp/domains/authorization';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { useTranslationWithPrefix } from '@hotjar/translations';

import { ForwardOption } from './ForwardOption';

interface ChannelObject {
  label: string;
  value: SlackApi.Channel;
}

export type { Settings };

export interface ForwardToSlackProps {
  onChange: (settings: Settings) => void;
  settings: Settings;
  header: React.ReactNode;
  subtext: React.ReactNode;
  currentSite: Site | undefined;
  upsell: (referrer: KnownReferrer) => void;
  onEnableSuccess: (message: string) => void;
  onEnableError: (message: string) => void;
  contextOfUse: ProductType;
  switchDisabled?: boolean;
  trackingState?: string;
}

const SlackIcon = styled(Icon)`
  margin-right: ${tokens.spacer.x1};
  /* NOTE: this margins combination aligns the icon with text vertically
   * and allows the text to float the icon from the right when there are two lines
   */
  margin-top: 2px;
  margin-bottom: 6px;
  float: left;
`;

const HeaderText = styled.span`
  margin-right: ${tokens.spacer.x1};
`;

const SelectWrapper = styled.div`
  padding: ${tokens.spacer.x1};
  position: relative;

  & .select-channel-loader {
    position: absolute;
    top: ${tokens.spacer.x3pt5};
    right: ${tokens.spacer.x2pt5};
  }
`;

const ErrorMessage = styled.div`
  color: ${tokens.color.text_danger};
  margin-top: ${tokens.spacer.x0};
  ${tokens.text('caption')};
`;

const channelsToSelectValues = (values: Array<SlackApi.Channel>): Array<ChannelObject> =>
  values.map((channel: SlackApi.Channel) => ({
    label: `#${channel}`,
    value: channel,
  }));

export const ForwardToSlack = ({
  onChange,
  settings,
  header,
  subtext,
  currentSite,
  upsell,
  onEnableSuccess,
  onEnableError,
  contextOfUse,
  switchDisabled = false,
  trackingState,
}: ForwardToSlackProps): JSX.Element => {
  const { t } = useTranslationWithPrefix('common:forwarding.slack');
  const { hasPermission } = useAuthorization();
  const hasSiteIntegrationsSlackEnrollPermission = hasPermission(
    'site',
    `${currentSite?.id}`,
    'site-integrations-slack-enroll',
  );
  const isMinimalPlan = currentSite ? SlackFeature.canUse(contextOfUse, currentSite) : false;

  const { id: siteId } = currentSite ?? {};

  // NOTE: the reason we cannot put `settings` as a dependency of this callback is
  // that it's always a new object, with every render (Angular recreates it)
  const { enabled: currentlyEnabled } = settings;
  const handleChange = useCallback(
    (newSettings) => {
      onChange(newSettings);

      if (currentlyEnabled !== newSettings.enabled) {
        tagRecording('forwarding_toggle');
      }
    },
    [currentlyEnabled, onChange],
  );
  const {
    state: {
      isLoading,
      isConnected,
      isEnabled,
      isModalOpen,
      hasError,
      hasApiError,
      allChannels,
      channels,
    },
    actions: { toggleModalOpen, setIsEnabled, setChannels, setHasError, setIsConnecting },
  } = useSlackState({
    settings,
    siteId,
    isMinimalPlan,
    onChange: handleChange,
  });

  const { click: trackClick } = useTrack();

  const onSlackEnableClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (!isMinimalPlan) {
      event.preventDefault();
      upsell(`${contextOfUse}.integrations_slack`);
    } else if (!isConnected) {
      event.preventDefault();

      if (hasSiteIntegrationsSlackEnrollPermission) {
        trackClick('Forward Toggle', 'Forward to Slack', {
          state_event_was_triggered_on: trackingState,
        });

        toggleModalOpen();
      }
    }
  };

  const onSlackEnableChange = (isChecked: boolean) => setIsEnabled(isChecked);

  const onConnect = () => setIsConnecting();

  const getSwitchTooltipContent = () => {
    if (!isMinimalPlan) {
      return t('minimal-plan-tooltip');
    }

    if (!hasSiteIntegrationsSlackEnrollPermission && !isConnected) {
      return t('admin-tooltip');
    }

    return '';
  };

  const getDisabledState = () => {
    if (switchDisabled) {
      return true;
    }

    if (!isMinimalPlan) {
      return true;
    }

    if (hasSiteIntegrationsSlackEnrollPermission) {
      return false;
    }

    if (!isConnected) {
      return true;
    }

    return false;
  };

  const headerElement = (
    <div>
      <SlackIcon icon="slack" size="m" />
      <HeaderText>{header}</HeaderText>
      {!isMinimalPlan && (
        <PlanBadge
          kind={SlackFeature.FEATURE_GROUP}
          size="s"
          onClick={() => upsell(`${contextOfUse}.integrations_slack`)}
        />
      )}
    </div>
  );

  const handleSelectedChannelsChange = useCallback(
    (selectedOptions: Array<{ value: SlackApi.Channel }>) => {
      const channelIds = selectedOptions.map(({ value }) => value);
      setChannels(channelIds);

      setHasError(channelIds.length === 0);
    },
    [setChannels, setHasError],
  );

  const inputElement = (
    <SelectWrapper>
      <MultiSelect
        // NOTE: this class is used for tests only! (data-testid doesn't work for some reason)
        // If you want to style things, look at styles prop
        className="slack-channel-select"
        onChange={handleSelectedChannelsChange}
        availableOptions={channelsToSelectValues(allChannels)}
        placeholder={t(isLoading ? 'loading' : 'placeholder')}
        selectedOptions={channelsToSelectValues(channels)}
        isInvalid={hasError || hasApiError}
      />
      <Loader className="select-channel-loader" isLoading={isLoading} />
      {hasApiError && <ErrorMessage>{t('api-error')}</ErrorMessage>}
      {hasError && <ErrorMessage>{t('error')}</ErrorMessage>}
    </SelectWrapper>
  );

  return (
    <ErrorBoundary squadSentryTag="integrations">
      <ForwardOption
        isEnabled={Boolean(isEnabled && isConnected)}
        onClick={onSlackEnableClick}
        onChange={onSlackEnableChange}
        header={headerElement}
        input={inputElement}
        switchDisabled={getDisabledState()}
        switchTooltipContent={getSwitchTooltipContent()}
        subtext={subtext}
      />
      <EnableSlackModal
        currentSite={currentSite}
        isOpen={isModalOpen}
        onRequestClose={toggleModalOpen}
        onConnect={onConnect}
        onSuccess={onEnableSuccess}
        onError={onEnableError}
      />
    </ErrorBoundary>
  );
};
